import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import Checkbox from "../Checkbox";
import { InfoIcon } from "../Icons";
import { format } from "date-fns";

import { actions } from "../../../Tickets/_redux/ticketRedux";
import RenderIf from "../../../../components/RenderIf";
import {
  randomGameOptionChooser,
  popularGameOptionChooser,
  idealGameOptionChooser,
  ticketPriceCalculator,
  predictionsMonitor,
  checkCirculationIsNotPlayed,
} from "../../helpers";
import {
  getCirculationGames,
  getExactTicketsCount,
  createTicket,
  getTicketById,
} from "../../requests";
import { actions as snackbarActions } from "app/hoc/Snackbar/snackbarRedux";
import { actions as authActions } from "app/modules/Auth/_redux/authRedux";

import "./PlayCirculation.css";
import TooltipComponent from "app/components/tooltip";
import MultiplySelect from "../MultiplySelect";

const selections = [
  {
    id: 1,
    label: "Populyar",
  },
  {
    id: 2,
    label: "İdeal",
  },
  {
    id: 3,
    label: "Təsadüfi oyun",
  },
];

const initialTimesValue = {
  label: "1X",
  value: 1
}

const PlayCirculation = ({ circulations, setShowPrint, refetch }) => {
  const [activeCirculationId, setActiveCirculationId] = useState(
    circulations?.[0]?.id
  );
  const [activeSelection, setActiveSelection] = useState(0);
  const [initialTicketGames, setInitialTicketGames] = useState([]);
  const [ticketGames, setTicketGames] = useState([]);
  const [ticketPrice, setTicketPrice] = useState(0);
  const [totalTicketPrice, setTotalTicketPrice] = useState(0);
  const [columnPrice, setColumnPrice] = useState();
  const [exactTicketCount, setExactTicketCount] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [times, setTimes] = useState(initialTimesValue);

  const dispatch = useDispatch();

  const circulation =
    useMemo(
      () =>
        circulations.find(
          (circulation) => circulation.id === activeCirculationId
        ),
      [circulations, activeCirculationId]
    ) ?? {};

  const selectorHandler = useCallback(
    (id) => {
      const selectionsHandlers = {
        1: () => {
          if (checkCirculationIsNotPlayed(ticketGames)) {
            // return toast.info("Bu tiraj uçun hələ bilet yaradılmayıb.")
          }

          const games = popularGameOptionChooser(initialTicketGames);
          const ticketPrice = ticketPriceCalculator(games, columnPrice);

          setTicketPrice(ticketPrice);
          setTicketGames(games);
        },
        2: () => {
          if (checkCirculationIsNotPlayed(ticketGames)) {
            // return toast.info("Bu tiraj uçun hələ bilet yaradılmayıb.")
          }

          const games = idealGameOptionChooser(initialTicketGames);
          const ticketPrice = ticketPriceCalculator(games, columnPrice);

          setTicketPrice(ticketPrice);
          setTicketGames(games);
        },
        3: () => {
          const games = randomGameOptionChooser(initialTicketGames);
          const ticketPrice = ticketPriceCalculator(games, columnPrice);

          setTicketPrice(ticketPrice);
          setTicketGames(games);
        },
      };
      selectionsHandlers[id]();

      setActiveSelection(id);
    },
    [initialTicketGames, columnPrice, ticketGames]
  );

  function handleChangeCirculation(id) {
    if (id !== activeCirculationId) {
      setActiveCirculationId(id);
      setTicketGames([]);
      setInitialTicketGames([]);
      setActiveSelection(0);
    }
  }

  function handleResetSelections() {
    setActiveSelection(0);
    setTicketGames(initialTicketGames);
    setTicketPrice(0);
  }

  function handleChangeGameOption(e, index) {
    setActiveSelection(0);
    const games = ticketGames.map((game, gameIndex) => {
      if (gameIndex === index)
        return {
          ...game,
          [e.target.name]: e.target.checked,
        };
      return { ...game };
    });
    const ticketPrice = ticketPriceCalculator(games, columnPrice);
    setTicketPrice(ticketPrice);
    setTicketGames(games);
  }

  function handleSubmit() {
    setIsSubmitting(true);
    const areAllMatchesPredicted = predictionsMonitor(ticketGames);
    if (!areAllMatchesPredicted) {
      dispatch(
        dispatch(
          snackbarActions.setSnackbar({
            variant: "error",
            message: "Bütün oyunlar üzrə proqnoz verilməlidir!",
          })
        )
      );
      setIsSubmitting(false);
    } else {
      const games = ticketGames.map((game) => ({
        gameId: game.id,
        option1: game.option1,
        optionX: game.optionX,
        option2: game.option2,
      }));
      const body  = {
        ticketGames: games,
        circulationId: circulation.id,
        type: activeSelection,
        times: times.value
      };
      createTicket(body)
        .then((res) => {
          getTicketById(res.data).then((res) => {
            handleResetSelections();
            dispatch(authActions.getProfile());
            dispatch(actions.setPrintType(0));
            dispatch(actions.setPrintData(res.data));
            dispatch(actions.setWinningPaid(false));
            setShowPrint(true);
          });
          setIsSubmitting(false);
          setTimes(initialTimesValue);
        })
        .catch((err) => {
          const message = err.response?.data?.message ?? "Xəta baş verdi";
          if(message === 'Mərc bitmişdir. Əməliyyat uğursuzdur.' || message === 'Tur hazırda aktiv deyil') {
            refetch();
          }
          dispatch(
            snackbarActions.setSnackbar({
              variant: "error",
              message,
            })
          );
          setIsSubmitting(false);
        });
    }
  }

  useEffect(() => {
    getCirculationGames(activeCirculationId)
      .then((res) => {
        const ticketGames =
          res.data?.games?.map((game) => ({
            ...game,
            option1: false,
            optionX: false,
            option2: false,
          })) || [];
        setColumnPrice(res.data?.columnPrice);
        setTicketGames(ticketGames);
        setInitialTicketGames(ticketGames);
      })
      .catch(() => null);
  }, [activeCirculationId]);

  useEffect(() => {
    const areAllMatchesPredicted = predictionsMonitor(ticketGames);
    if (areAllMatchesPredicted) {
      getExactTicketsCount(ticketGames, circulation.id)
        .then((res) => setExactTicketCount(res.data))
        .catch(() => null);
    }
  }, [ticketGames, circulation.id]);

  useEffect(() => {
    if(ticketPrice) {
      setTotalTicketPrice(ticketPrice * times.value)
    }
  }, [times, ticketPrice])

  const tooltipData = [
    {
      id: 1,
      title: "Populyar seçim nədir?",
      description:
        "Oyunçular tərəfindən 13 oyun üçün ən çox işarələnən seçimlərdən ibarət bir sütundur.Məs: Barselona və Real Madrid oyununda oyunçuların 70%i Barselona komdandasının qələbəsini, 20%i heç-heçə, 10%-i isə Real Madrid qələbəsini işarələyibsə, siz Populyar seçim seçdikdə Barselona qələbəsi üçün olan 1 xanası işarələnəcəkdir.",
    },
    {
      id: 2,
      title: "İdeal seçim nədir?",
      description:
        "13 oyunluq turda ilk 8 oyun üçün ən çox seçim faizi olan xana, növbəti 3 oyun üçün seçim faizi ikinci yerdə olan xana, son 2 oyun üçün isə ən aşağı seçim faizi olan xanalar işarələnməklə ideal seçim formalaşdırılır.",
    },
  ];

  return (
    <div className="circulation__wrapper">
      <div className="circulation">
        <div className="circulation__tabs">
          {circulations?.map((circulation, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleChangeCirculation(circulation.id)}
              className={`circulation__tab ${
                activeCirculationId === circulation.id
                  ? "circulation__tab-active"
                  : ""
              }`}
            >
              {circulation.productName}
            </button>
          ))}
        </div>
        <div className="circulation__details">
          <div className="circulation__details-item">
            <span className="circulation__details-title">Tur nömrəsi</span>
            <span className="circulation__details-description">
              {circulation.code}
            </span>
          </div>
          <div className="circulation__details-item">
            <span className="circulation__details-title">Hovuz məbləği</span>
            <span className="circulation__details-description">
              {circulation.totalAmount && circulation.totalAmount.toFixed(2)} AZN
            </span>
          </div>
          <div className="circulation__details-item">
            <span className="circulation__details-title">
              Mərcin bitmə tarixi
            </span>
            <span className="circulation__details-description circulation__details-description--blue">
              {circulation.expiredDate &&
                format(new Date(circulation.expiredDate), "dd-MM-yyyy HH:mm")}
            </span>
          </div>
          <div className="circulation__details-item">
            <span className="circulation__details-title">
              Turun bitmə tarixi
            </span>
            <span className="circulation__details-description circulation__details-description--red">
              {circulation.resultedDate &&
                format(new Date(circulation.resultedDate), "dd-MM-yyyy HH:mm")}
            </span>
          </div>
        </div>
        <div className="circulation__games">
          <div className="circulation__games-header">
            <div></div>
            <div className="pl-2">1</div>
            <div className="pl-2">x</div>
            <div className="pl-2">2</div>
          </div>
          <div className="circulation__games-body">
            {ticketGames.map((game, index) => (
              <div key={game.id} className="circulation__game">
                <div className="circulation__game-text">
                  <p className="circulation__game-title">
                    {game.hostClubName} - {game.guestClubName}
                  </p>
                  <p className="circulation__game-date">
                    <span>
                      {format(new Date(game.startDate), "dd-MM-yyyy")}
                    </span>
                    <span>{game.startDate ? format(new Date(game.startDate), "HH:mm") : ""}</span>
                  </p>
                </div>
                <div className="d-flex align-items-center circulation__game-selection">
                  <span className="mobile-option-name">1</span>
                  <Checkbox
                    className="mr-2"
                    name="option1"
                    checked={ticketGames[index]?.option1}
                    onChange={(e) => handleChangeGameOption(e, index)}
                  />
                  <span>
                    {Boolean(
                      game.option1Ratio ||
                        game.optionXRatio ||
                        game.option2Ratio
                    ) && `${game.option1Ratio}%`}
                  </span>
                </div>
                <div className="d-flex align-items-center circulation__game-selection">
                  <span className="mobile-option-name">x</span>
                  <Checkbox
                    className="mr-2"
                    name="optionX"
                    checked={ticketGames[index]?.optionX}
                    onChange={(e) => handleChangeGameOption(e, index)}
                  />
                  <span>
                    {Boolean(
                      game.option1Ratio ||
                        game.optionXRatio ||
                        game.option2Ratio
                    ) && `${game.optionXRatio}%`}
                  </span>
                </div>
                <div className="d-flex align-items-center circulation__game-selection">
                  <span className="mobile-option-name">2</span>
                  <Checkbox
                    className="mr-2"
                    name="option2"
                    checked={ticketGames[index]?.option2}
                    onChange={(e) => handleChangeGameOption(e, index)}
                  />
                  <span>
                    {Boolean(
                      game.option1Ratio ||
                        game.optionXRatio ||
                        game.option2Ratio
                    ) && `${game.option2Ratio}%`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <RenderIf condition={Boolean(exactTicketCount)}>
        <div className="d-flex gap-2">
          <span className="fw-bold mr-2">Eyni bilet sayı: </span>
          <span className="text-red">{exactTicketCount}</span>
        </div>
      </RenderIf>
      <div className="d-flex gap-2">
        <span className="fw-bold mr-2">Sütun üzrə mərc məbləği: </span>
        <span className="text-red">{columnPrice} AZN</span>
      </div>
      <div className="d-flex gap-2">
        <span className="fw-bold mr-2">Cəmi mərc məbləği: </span>
        <span className="text-red">
          {Boolean(totalTicketPrice) && `${totalTicketPrice.toFixed(2)} AZN`}
        </span>
      </div>
      <div className="toolbar">
        <div className="toolbar__inner">
          <div className="d-flex gap-2 align-items-end">
            <div className="toolbar__quick-selections">
              <div className="toolbar__info">
                <span className="mr-1">Aşağıdakı hazır seçimlər ilə sürətli mərc edin</span>
                <TooltipComponent data={tooltipData}>
                  <InfoIcon />
                </TooltipComponent>
              </div>
              <div className="d-flex mr-2">
                {selections.map((button) => (
                    <button
                        key={button.id}
                        onClick={() => selectorHandler(button.id)}
                        className={`toolbar__selection-button mr-2 ${
                            button.id === activeSelection
                                ? "toolbar__selection-button-active"
                                : ""
                        }`}
                    >
                      {activeSelection === button.id && (
                          <svg className="animated-check" viewBox="0 0 24 24">
                            <path d="M4.1 12.7L9 17.6 20.3 6.3" fill="none" />
                          </svg>
                      )}
                      {button.label}
                    </button>
                ))}
              </div>
            </div>
            <MultiplySelect onChange={(value) => setTimes(value)} value={times}/>
          </div>
          <div className="d-flex gap-2">
            <button
              onClick={handleResetSelections}
              className="toolbar__button-light"
            >
              Seçimləri sıfırla
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="toolbar__button"
            >
              Mərc et
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayCirculation;
